import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { analyseFaceObstruction } from '../face/obstruction.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Foreign-object check.
 *
 * Hair is excluded by construction (it has its own check): only large connected
 * regions inside the face ellipse that are neither skin nor hair are counted,
 * with sharp edges or a strong left/right asymmetry. A hard failure needs high
 * coverage *and* a confident measurement *and* sharp edges — a soft shadow must
 * never reject a photo.
 */
export const faceObstructionCheck: ValidationCheck = {
  id: 'face_obstruction',
  title: 'Лицо не закрыто посторонними предметами',
  description:
    'Ищет крупные связные области на лице, которые не являются кожей или волосами (маска, рука, микрофон, телефон).',
  kind: 'content',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, faceObstructionCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    const pixels = ctx.pixels;
    if (!geometry || !pixels) {
      return notChecked(
        ctx,
        faceObstructionCheck,
        'Нет данных для анализа посторонних предметов на лице.',
        ctx.rule.severity,
      );
    }

    const minCoverage = numberParam(ctx.rule.params, 'minCoverage', 0.05);
    // `maxCoveredRatio` is the profile-facing alias of the failure floor.
    const maxCoverage = numberParam(
      ctx.rule.params,
      'maxCoveredRatio',
      numberParam(ctx.rule.params, 'maxCoverage', 0.15),
    );
    const minAsymmetry = numberParam(ctx.rule.params, 'minAsymmetry', 0.3);
    const minEdgeSharpness = numberParam(ctx.rule.params, 'minEdgeSharpness', 0.5);
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.6);

    const analysis = analyseFaceObstruction({ pixels, geometry });
    const confident = analysis.confidence >= uncertainBelow;
    const sharp = analysis.boundarySharpness >= minEdgeSharpness;
    const asymmetric = analysis.asymmetry >= minAsymmetry;
    const detected = analysis.coverage >= minCoverage && (sharp || asymmetric);

    const status: CheckResult['status'] = !detected
      ? 'pass'
      : analysis.coverage >= maxCoverage && confident && sharp
        ? 'fail'
        : confident
          ? 'warning'
          : 'uncertain';

    const message = (() => {
      if (status === 'pass') return '✅ Посторонних предметов на лице не обнаружено';
      if (status === 'fail') return '❌ Лицо закрыто посторонним предметом';
      if (status === 'uncertain') {
        return '⚠ Не удалось надёжно определить, закрыто ли лицо посторонним предметом';
      }
      return '⚠ Возможно лицо частично закрыто';
    })();

    const covered = analysis.largest;

    return plainResult({
      id: faceObstructionCheck.id,
      title: faceObstructionCheck.title,
      status,
      severity: ctx.rule.severity,
      value: analysis.coverage,
      max: maxCoverage,
      unit: 'ratio',
      ...(ctx.rule.official ? { official: ctx.rule.official } : {}),
      message,
      ...(ctx.rule.official?.statement ? { requirement: ctx.rule.official.statement } : {}),
      fixability: 'retake',
      geometry: covered
        ? [
            {
              type: 'rect' as const,
              coordinates: [
                roundTo(covered.rect.x, 4),
                roundTo(covered.rect.y, 4),
                roundTo(covered.rect.width, 4),
                roundTo(covered.rect.height, 4),
              ],
            },
          ]
        : [],
      problemRegions:
        status === 'pass' || !covered
          ? []
          : [
              {
                id: 'face_obstruction_area',
                rect: covered.rect,
                message: 'Возможная область перекрытия лица посторонним предметом.',
                severity: status === 'fail' ? ('error' as const) : ('warning' as const),
              },
            ],
      confidence: analysis.confidence,
      details: {
        coverage: analysis.coverage,
        componentCount: analysis.componentCount,
        asymmetry: analysis.asymmetry,
        boundarySharpness: analysis.boundarySharpness,
        scannedPixels: analysis.scannedPixels,
        minCoverage,
        maxCoverage,
        minAsymmetry,
        minEdgeSharpness,
        uncertainBelowConfidence: uncertainBelow,
        mode: analysis.mode,
        notes: analysis.notes,
        displayValue: status === 'pass' ? 'не обнаружено' : 'возможное перекрытие',
      },
    });
  },
};
